// Cycle 407 — the treating screen, and the check-in that asks before it
// gets loud.
//
// Mounted by NowScreen while DECLARED custody is open on the active member's
// urgent low, in place of the alarm takeover it transforms from. It is NOT
// Stop and it is not a snooze: the display never stops, it transforms; the
// quiet is bounded (the ring) and verified (the check-in); and the two
// actions at the bottom are on screen at all times.
//
// ⚠ THE GLUCOSE IS THE ARBITER. Nothing here decides anything about the
// number. "Yes" is sent to the server, which reads the live readings under
// the row lock and answers with what it DID; this screen speaks that.
//
// ⚠ MEMBER-SCOPED. Everything rendered comes from `episode` (the ACTIVE
// member's alarm state) and the active member's own reading. A second
// member's alarm is never gated by this window: `useAlarmEpisode` derives
// per active member, and the socket clears the check-in event on switch.
//
// ⚠ THE CHECK-IN SPEECH IGNORES `tts_quiet_hours` — see lib/treatingCheckin.
// This component never imports lib/quietHours, and a source guard pins it.
//
// Cycle 409: it is ALSO mounted for a QUIET window (a snooze on an urgent
// low) — same ring, same two loud actions, plus "I'm treating this" — with
// copy that never calls a snooze "treating". `episode.treating` (declared
// only) is still what every gate reads; `episode.quietWindow` only shows.

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { recoveringHeaderLine } from "../../lib/alarmStandDown";

import { TrendArrow } from "../glucose/TrendArrow";
import { BolusRings } from "../glucose/BolusRings";
import {
  CORRECTION_BOLUS_COLOR,
  basalRingColor,
  formatBasalHoursLeft,
  iobTimeRemainingMs,
} from "../../lib/iob";
import type { BasalContext, BolusForRings } from "../../lib/iob";
import type { AlarmEpisodeState } from "../../hooks/useAlarmEpisode";
import type { FrameVariant } from "../../hooks/useFrameVariant";
import {
  displayValue,
  fmtAge,
  trendInfo,
  zoneColor,
  zoneOf,
} from "../../lib/glucose";
import { fmtTreatingRemaining } from "../../lib/treating";
import { coarseLeft, iobChipEntries, type IobChipEntry } from "../../lib/iobChip";
import {
  COMPACT_CENTRE,
  COMPACT_PAD_Y,
  MOBILE_TIMER_COMPACT_MIN_H,
  chooseIobFit,
  compactCanvas,
  type IobRoom,
  type IobStep,
} from "../../lib/iobCardFit";
import { startedLine, tookItLine } from "../../lib/attributionPicker";
import type { TreatingBy } from "../../lib/types";
import { HeroAvatar } from "../ui/HeroAvatar";
import { useIsLandscape } from "../../hooks/useIsLandscape";
import { recordClientEvent } from "../../lib/clientEvents";
import {
  claimPromptRender,
  claimPromptSpoken,
  promptClaimKey,
  CHECKIN_REPLY_VISIBLE_MS,
  CHECKIN_REPROMPT_MS,
  checkinKey,
  checkinQuestionText,
  checkinReplyIsTransient,
  checkinReplyText,
  custodyScreenCopy,
  fmtPromptRemaining,
  helpMetaLine,
  noDataValueLine,
  promptRemainingMs,
  ringFraction,
  ringTotalMs,
  worseningMetaLine,
} from "../../lib/treatingCheckin";
import type { DisplayGlucose } from "../../lib/currentGlucose";
import type {
  CustodyCheckinAnswer,
  CustodyCheckinOutcome,
  Thresholds,
} from "../../lib/types";
import styles from "./TreatingScreen.module.css";

interface Props {
  variant: FrameVariant;
  memberName: string | null;
  /** ⚠ Cycle 435: the number on the treating screen is a `DisplayGlucose`
   *  the selector released as current, or null — No Data mode. ONLY the
   *  number: the ring, the prompt, every button and the tone are driven
   *  by `episode` and never by this. A raw `Reading` cannot be passed. */
  current: DisplayGlucose | null;
  /** The age of the reading the selector judged (current or withheld), so
   *  No Data mode still says how old the last value is. */
  ageMinutes: number | null;
  units: "mg/dL" | "mmol/L";
  thresholds: Thresholds;
  episode: AlarmEpisodeState;
  now: number;
  /** The app-level TTS `speak` (one owner, already primed by the tap that
   *  opened the window). Optional: if TTS is unavailable the chime and the
   *  visual carry the check-in — no speech path is load-bearing. */
  speak?: (text: string) => void;
  /** Cycle 409: on a QUIET window (a snooze) the screen offers "I'm
   *  treating this" — the one tap that converts a reflex into a
   *  declaration (and with it the check-in and the extensions). Wired to
   *  NowScreen's `beginTreating`, so it is the same door as the takeover's
   *  button. Absent on a declared window. */
  onDeclare?: () => void;
  /** The declare request's in-flight / refusal state, owned by NowScreen
   *  (the same `treatingBusy` / `treatingError` the takeover uses) — so a
   *  refusal on a dark feed (`no_fresh_reading`) is SAID here, not lost
   *  in a branch this screen pre-empts. */
  declareBusy?: boolean;
  declareError?: string | null;
  /** Cycle 437: the insulin-on-board gauge's data, computed by NowScreen
   *  from the SAME `activeBolusesForRings` / `basalForContext` pair the Now
   *  card reads — never a second derivation. The ring-exclusion invariant
   *  holds by construction: a basal never enters `boluses`. */
  boluses?: BolusForRings[];
  basal?: BasalContext | null;
  /** Cycle 456: who took the window (server-resolved), or null. */
  treatingBy?: TreatingBy | null;
  /** Cycle 456: this device's signed-in account IS the treater. */
  selfTreating?: boolean;
  /** Cycle 456: "Not Maya?" — reopens the picker for this window (kiosk
   *  and web). Absent on phones. */
  onNotTreater?: () => void;
  /** Cycle 456: the "Who's treating?" sheet. When present it takes the
   *  place of the main area (glucose, instruments, meta lines) — an in-flow
   *  sibling ABOVE the actions row, so "I need help now" and "Resume
   *  alerts" stay below it, visible and working, by construction. */
  picker?: ReactNode;
}


/* ⚠ Cycle 459: `MOBILE_STACK_MIN_H` (470) IS GONE, AND IT WAS THE GATE.
 *
 * It withheld the phone gauge whenever `.main` was under 470, which on the
 * attributed treating screen is every phone but the largest (measured:
 * 430x932 main 550, 390x844 462, 375x812 430, 360x740 358). The phone now
 * FITS the card instead, in three steps chosen from measured room by
 * `chooseIobFit` in `lib/iobCardFit.ts` (full / compact / chip, then the
 * chip beside the cycle-451 compact timer). The hero coupling the constant
 * carried is now a measurement: the chooser reads the hero's real height, so
 * a hero size change moves the decision with it. See the 0.447.0 entry.
 *
 * The kiosk/web 386 floor (`KIOSK_INSTRUMENT_COLUMN_MIN_H`, cycle 437) and
 * `MOBILE_TIMER_COMPACT_MIN_H` (252, cycle 451) moved to the same module,
 * unchanged: the shortest phone with a question up still compacts the timer
 * whatever the IOB step. Its measurements and the arc arithmetic for the 72px
 * ring stay in the stylesheet beside `.mobile.compactTimer`. */

const RING_R = 52;
const RING_C = 2 * Math.PI * RING_R;


export function TreatingScreen({
  variant,
  memberName,
  current,
  ageMinutes,
  units,
  thresholds,
  episode,
  now,
  speak,
  onDeclare,
  declareBusy = false,
  declareError = null,
  boluses = [],
  basal = null,
  treatingBy = null,
  selfTreating = false,
  onNotTreater,
  picker,
}: Props) {
  const quiet = episode.custodyKind === "quiet";
  const sgv = current?.sgv ?? null;
  const zone = zoneOf(sgv, thresholds);
  // Cycle 427: NO DATA MODE is a rendering state (never a custody state,
  // never a column): the ring keeps counting; every button stays.
  // Cycle 435: it is entered exactly when the selector released no current
  // value — a stale sample, a server `stale` verdict, or a confirmation
  // that expired all land here — and the number is WITHHELD ("—") with its
  // age beside it, rather than greyed and still legible.
  const noData = sgv == null;
  const color = noData ? "var(--ink-3)" : zoneColor(zone);
  const valueText = sgv != null ? displayValue(sgv, units) : "—";
  const trend = trendInfo(current?.trend ?? null, current?.trendRate ?? null);
  const ageMin = ageMinutes;

  const remainingMs = episode.treatingRemainingMs ?? 0;
  const ringTotal = ringTotalMs();
  const frac = ringFraction(remainingMs, ringTotal);
  const dashOffset = RING_C * (1 - frac);

  // ⚠ PER-ACTION in-flight state. One shared `busy` would let a hung
  // "yes" disable "I need help now" and "Resume alerts" — the two escape
  // hatches — for as long as the socket hangs (the cycle-407 safety
  // review's V3). Each button is disabled only while ITS OWN request is
  // in flight; the loud actions are never gated by the quiet ones.
  const [busy, setBusy] = useState<CustodyCheckinAnswer | null>(null);
  const [reply, setReply] = useState<string | null>(null);
  const replyClearTimerRef = useRef<number | null>(null);

  /** ⚠ Cycle 437: a reply line with an on-screen surrogate has a bounded
   *  life; one WITHOUT a surrogate stands until the next question or the
   *  window ends. `checkinReplyIsTransient` in treatingCheckin.ts makes that
   *  call BY OUTCOME, beside the words — it is not a property of the call
   *  site. The two failure lines authored here ("Could not send.", the
   *  timeout) are never transient; the cycle-416 rule is not relaxed.
   *
   *  ⚠ Do NOT re-justify a transient line by "it is also spoken" — this
   *  component declares no speech path load-bearing, and the C378 voice
   *  failure is silent. The on-screen surrogate is what carries it.
   *
   *  WHY ANY OF IT EXISTS: the reply line is its own row and costs 30px of
   *  the screen's vertical stack. It was cleared ONLY by the next check-in
   *  event, so after the first answer it sat for the rest of the window —
   *  and on the kiosk that 30px is the difference between a `.main` row of
   *  402 (the instrument column's 386 fits, 16px spare) and 372 (it does
   *  not). So answering a check-in at minute three removed the insulin
   *  readout until minute ten, with no way to get it back. */
  /** ⚠ THE ONLY DOOR TO THE REPLY LINE. Every caller goes through it, and it
   *  ALWAYS cancels a pending auto-clear before doing anything else.
   *
   *  That cancel is the load-bearing part. The first version left the two
   *  failure sites on the raw `setReply`, so a timer armed by an EARLIER
   *  success fired at its own deadline and wiped a LATER failure: answer Yes
   *  at t=0 (8 s armed), tap "I need help now" at t=2 s, its POST fails, and
   *  at t=8 s "Could not send." is erased by the success's timer. On the
   *  "Resume alerts" variant the caregiver is left believing alerts came back
   *  when they did not — a failed ack made invisible by the very mechanism
   *  whose comment promised it would not be. Found by the safety review. */
  const showReply = useCallback(
    (line: string | null, opts?: { transient?: boolean }) => {
      if (replyClearTimerRef.current != null) {
        window.clearTimeout(replyClearTimerRef.current);
        replyClearTimerRef.current = null;
      }
      setReply(line);
      if (line == null || !opts?.transient) return;
      replyClearTimerRef.current = window.setTimeout(() => {
        replyClearTimerRef.current = null;
        setReply(null);
      }, CHECKIN_REPLY_VISIBLE_MS);
    },
    [],
  );

  // ─── Speech: once per POSED question, keyed, with one local re-prompt ──
  const repromptTimerRef = useRef<number | null>(null);
  const pendingRef = useRef(episode.checkinPending);
  pendingRef.current = episode.checkinPending;
  const question = episode.promptKind;
  const contactName = episode.treatingContactName;
  const questionText =
    question != null
      ? checkinQuestionText(question, memberName, quiet ? null : contactName)
      : null;
  // Cycle 427: the prompt's server-authored deadline, counted down on this
  // device's own clock — the dead-man-switch shape: a missed frame never
  // leaves a prompt hanging (the server resolves it at the deadline).
  const promptLeftMs = promptRemainingMs(now, episode.promptDeadline);

  // ── Cycle 447: claim the render ──────────────────────────────────────
  // ⚠ TELEMETRY ONLY — the one change this cycle makes to this screen.
  // Nothing rendered, gated or sounded here moves. The server issues a
  // prompt and cannot see whether a surface drew it; this device says it
  // did, once per prompt (keyed on kind + the server's deadline, never on
  // the countdown tick), as a client-claimed ledger row on the next
  // activation report. The episode report reads it to flag "a prompt
  // issued with no render row". `surface` tells the kiosk apart from the
  // phone's treating screen: on 2026-09-17 every answered prompt came from
  // the kiosk and the phone was never proven to have shown one.
  //
  // ⚠ CYCLE 451 — THE CLAIM IS MODULE-LEVEL NOW, NOT A REF ON THIS
  // COMPONENT. A ref is this surface's lifetime, and from this cycle the
  // surface can be left: the rail and the tab bar are back during a
  // bedside window, so a trip to History and back re-mounted the screen
  // and claimed the same prompt twice. See `claimPromptRender`.
  useEffect(() => {
    if (!episode.checkinPending || !question) return;
    const key = promptClaimKey(episode.alertId, question, episode.promptDeadline);
    if (key == null || !claimPromptRender(key)) return;
    recordClientEvent("prompt_rendered", {
      kind: question,
      surface: variant === "kiosk" ? "kiosk" : "treating",
      prompt_deadline: episode.promptDeadline ?? null,
    });
  }, [
    episode.checkinPending,
    episode.alertId,
    question,
    episode.promptDeadline,
    variant,
  ]);

  useEffect(() => {
    const ev = episode.checkinEvent;
    if (!ev) return;
    const key = checkinKey(ev.alertId, ev.question, ev.expiresAt);
    // ⚠ Cycle 451: module-level, not the component ref — a return to /now
    // must not re-ask a question this device already spoke. See
    // `claimPromptSpoken`.
    if (!key || !claimPromptSpoken(key)) return;
    const text = checkinQuestionText(ev.question, memberName, quiet ? null : contactName);
    showReply(null);
    speak?.(text);
    if (repromptTimerRef.current != null) {
      window.clearTimeout(repromptTimerRef.current);
    }
    // Silence at a question is never yes: ONE local re-prompt, then the
    // server resolves the prompt at its deadline (expiry for the check-in;
    // the help path or a fresh search for the two 60 s prompts).
    repromptTimerRef.current = window.setTimeout(() => {
      repromptTimerRef.current = null;
      if (pendingRef.current) speak?.(text);
    }, CHECKIN_REPROMPT_MS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [episode.checkinEvent?.seq]);

  useEffect(
    () => () => {
      if (repromptTimerRef.current != null) {
        window.clearTimeout(repromptTimerRef.current);
      }
      if (replyClearTimerRef.current != null) {
        window.clearTimeout(replyClearTimerRef.current);
      }
    },
    [],
  );

  const send = useCallback(
    async (answer: CustodyCheckinAnswer) => {
      if (busy === answer) return;
      setBusy(answer);
      let outcome: CustodyCheckinOutcome | "timeout" | null = null;
      try {
        // The hook races the POST against CHECKIN_REQUEST_TIMEOUT_MS and
        // un-hides the question on timeout; this control re-enables. A
        // quiet answer names the prompt on screen (cycle 427).
        outcome = await episode.checkin(answer, question);
      } finally {
        setBusy((b) => (b === answer ? null : b));
      }
      if (outcome === "timeout") {
        showReply("Taking longer than usual. Try again.");
        return;
      }
      if (outcome == null) {
        // A refused or failed send. The screen cannot tell a 409 (the
        // window already ended) from a network fault, so it says only
        // what it knows — spoken too, since this is the one case a
        // caregiver may have looked away; the row's next frame carries
        // the truth.
        showReply("Could not send.");
        speak?.("Could not send.");
        return;
      }
      // Cycle 450: the minutes left ride the server's `custody_until` on
      // the bound row (read for the `capped` refusal line only).
      const line = checkinReplyText(outcome, memberName, episode.treatingRemainingMs);
      // Classified by what the outcome MEANS, in treatingCheckin.ts beside
      // the words — never by "the POST returned something".
      showReply(line, { transient: checkinReplyIsTransient(outcome) });
      if (line) speak?.(line);
    },
    [busy, episode, memberName, speak, question, showReply],
  );

  const extensions = episode.treatingExtensionCount;
  const copy = custodyScreenCopy(
    episode.custodyKind,
    memberName,
    episode.treatingContactName,
  );
  // Cycle 428 — v3 "Coming back up", TEXT ONLY: while the latest reading is
  // at or above the low line and the row is still open, the header says why
  // this screen is still up with the number back in range. Same ring, same
  // buttons, same (silent, windowed) sound state as the reading before.
  const tag = episode.recoveringFromUrgentLow
    ? recoveringHeaderLine(memberName)
    : copy.tag;

  // ─── Cycle 409: "I need help now" only when there is somebody to ask ──
  // The help text goes to every OTHER verified hero; in a one-hero
  // household (or with the only other hero as the acceptor) it can reach
  // nobody, and the button then does exactly what "Resume alerts" does
  // while promising more (2026-08-31: `backups_unsent … no_recipients`).
  // Client-side, best-effort, fails toward SHOWING the button (a fetch
  // fault must never hide a loud control); the server stays the truth.
  // Cycle 409 (re-review): the reach is SERVER-computed on the payload
  // (`custody_backup_reach` — verified heroes minus a named acceptor, the
  // exact recipient rule of the help text), so the screen never pulls the
  // contact list into the alarm surface and never guesses by name. Fails
  // toward SHOWING: `null` (unknown, or no window yet) keeps the button.
  const canAskForHelp = episode.backupReach == null || episode.backupReach > 0;
  // Cycle 427: the meta row's one extra line — the worsening prompt's
  // "<member> dropped to N" (the live value the frames carry, never a
  // claimed one) or the help path's "Backup requested — calling <hero>…".
  // Copy only, in the meta row's existing slot: no new layout.
  const helpLine = helpMetaLine(episode.helpState, episode.helpContactName);
  // ⚠ Cycle 437: whether there is room for the SECOND instrument is
  // MEASURED off the real row, never inferred from a device class or a
  // media query. `kioskStageHeight()` answers "what stage would this device
  // class get", which is not the same question and reads 600 on anything
  // that is not an Android tablet — including the harness and the bench
  // `?kiosk=1` path, where the stage really is 640. And a media query
  // resolves against the true viewport, not the transform-scaled design box
  // (the cycle-395 trap). So: observe the row.
  //
  // ⚠ CYCLE 451 — `offsetHeight`, NEVER `getBoundingClientRect()`. This is
  // the cycle-400 trap, and it had the withhold switched off on the entire
  // fleet. `getBoundingClientRect()` reports SCREEN px; the kiosk stage is
  // painted through `transform: scale()`, so on the 1920×1200 panels a row
  // 344 design px tall measures 645 and sails past a 386 DESIGN-px floor.
  // Measured on the real frame: with the check-in panel open at three
  // rings the instrument column kept its gauge and overflowed the centred
  // row by 21 px out each end (a centred flex row pushes overflow out BOTH
  // ways, so nothing scrolls and nothing looked wrong to a scroll check),
  // putting the gauge card over the Yes/No answers. `offsetHeight` is
  // layout px in the design box, so the comparison is finally like-for-
  // like; at scale 1 (the 1024×600 box, every phone) it is the same number
  // cycle 437 measured, so those surfaces are unchanged.
  //
  // ⚠ CYCLE 459 — THE OBSERVER FOLLOWS THE ROW, NOT THE FIRST ONE IT SAW.
  // `.main` unmounts while "Who's treating?" covers it and a NEW element
  // mounts when the sheet closes. The cycle-437 effect attached once, at
  // mount, so on the device that answered the sheet it kept watching the
  // detached row (which reports 0, i.e. "unknown") and a phone withheld the
  // card for the rest of the window at any height. The element now arrives
  // through a callback ref and the observer re-attaches to whichever row is
  // on screen.
  const isLandscape = useIsLandscape();
  const [mainEl, setMainEl] = useState<HTMLDivElement | null>(null);
  const mainRef = useCallback((el: HTMLDivElement | null) => setMainEl(el), []);
  const heroRowRef = useRef<HTMLDivElement | null>(null);
  const heroCaptionRef = useRef<HTMLDivElement | null>(null);
  const timerCardRef = useRef<HTMLDivElement | null>(null);
  const [room, setRoom] = useState<IobRoom | null>(null);
  useLayoutEffect(() => {
    const el = mainEl;
    if (!el || typeof ResizeObserver === "undefined") return;
    // Every field is a quantity no IOB step changes (lib/iobCardFit.ts):
    // the row is the root column's only grower (supply); the hero's row and
    // caption are untouched by every step; the timer is `flex: 0 0 auto`.
    // The hero WIDTH is the larger scrollWidth, which is the content width
    // even when a landscape row squeezes the box.
    const read = () => {
      const row = heroRowRef.current;
      const cap = heroCaptionRef.current;
      const next: IobRoom = {
        mainH: el.offsetHeight,
        mainW: el.offsetWidth,
        heroH: (row?.offsetHeight ?? 0) + (cap?.offsetHeight ?? 0),
        heroW: Math.max(row?.scrollWidth ?? 0, cap?.scrollWidth ?? 0),
        timerW: timerCardRef.current?.offsetWidth ?? 0,
      };
      setRoom((prev) =>
        prev &&
        prev.mainH === next.mainH &&
        prev.mainW === next.mainW &&
        prev.heroH === next.heroH &&
        prev.heroW === next.heroW &&
        prev.timerW === next.timerW
          ? prev
          : next,
      );
    };
    read();
    const ro = new ResizeObserver(read);
    ro.observe(el);
    for (const r of [heroRowRef, heroCaptionRef, timerCardRef]) {
      if (r.current) ro.observe(r.current);
    }
    return () => ro.disconnect();
  }, [mainEl]);
  const mainH = mainEl != null && room != null ? room.mainH : null;
  // ⚠ ZERO IS UNKNOWN, NOT "TOO SMALL". A 0px measurement means the row was
  // never laid out — an unmounted subtree, or the node test harness, whose
  // DOM shim returns a zero rect for everything by construction. Reading it
  // as "no room" makes the gauge unreachable to every behavioural test,
  // which is how the cycle-437 acceptance pins first shipped VACUOUS: the
  // break that added an onTap to the gauge passed, because there was no
  // gauge in the tree to have one.
  //
  // Cycle 459: what is on board, how many rings the card draws and how many
  // rows the chip draws. The chip's rows are `iobChipEntries` (the sheet's
  // own rule: two boluses and one long-acting, coloured by type) plus a DUE
  // long-acting, which that rule leaves out and the card still draws.
  const chipEntries = iobChipEntries(boluses, basal, now);
  const dueBasal = basal != null && basal.due ? basal : null;
  const onBoard = boluses.length > 0 || basal != null;
  const ringCount = boluses.length + (basal != null ? 1 : 0);
  const iobFit = chooseIobFit({
    variant,
    onBoard,
    ringCount,
    entryCount: chipEntries.length + (dueBasal ? 1 : 0),
    landscape: isLandscape,
    room: mainH != null && mainH > 0 ? room : null,
  });
  // ⚠ THE UNKNOWN CASE FAILS SAFE IN THE DIRECTION THAT MATTERS, WHICH IS
  // NOT THE SAME DIRECTION ON BOTH SURFACES.
  //
  // `mainH` is unknown before the first measurement, and stays unknown where
  // `ResizeObserver` does not exist. Rendering the gauge then is harmless on
  // the kiosk — 640 is the only fit box Decision 1 keeps and it has the room,
  // so a short stage hides it on the next frame rather than the common case
  // flashing empty. On MOBILE the same guess is the hazard cycle 437 had to
  // fix once: the gauge card's `position: relative` root paints over
  // "I need help now" and TAKES ITS HIT TEST (measured 6.9px of the loud
  // action dead, 15.3px with the recovering banner). So on mobile an
  // unmeasured row withholds every step for that one frame; the worst case
  // is a missing instrument for one frame, never an unpressable alarm
  // control. Flagged LOW by the security review. `chooseIobFit` applies the
  // same rule; this is the statement of it the cycle-437 pin reads.
  const roomUnknown = mainH == null || mainH <= 0;
  const roomForGauge = roomUnknown
    ? variant !== "mobile"
    : iobFit.step !== "withheld";
  const iobShown =
    roomForGauge &&
    (iobFit.step === "full" || iobFit.step === "compact" || iobFit.step === "chip");
  // Cycle 451: the shortest phone with a question up. Unknown never
  // compacts — the common case must not flash the small ring.
  //
  // ⚠ PORTRAIT ONLY, AND THE GATE IS EXPLICIT. Phone LANDSCAPE has its own
  // compaction (the `max-height: 460px` block) and a row of ~148px, so it
  // would match this threshold ALWAYS — and a top-level `.mobile
  // .compactTimer` rule (0,3,0) out-specifies that block's rules (0,2,0)
  // and would silently govern the tightest orientation in the file. The
  // class name says "the shortest phone, with a question on screen"; this
  // is what makes that true. Flagged by `safety-invariant-reviewer`.
  //
  // Cycle 459 (operator decision): the same treatment ALSO applies when the
  // IOB chip fits only beside the compact timer. Both reasons are decided
  // from supply (the row) and never from the timer itself, so neither can
  // feed back into its own input.
  const compactTimer =
    variant === "mobile" &&
    !isLandscape &&
    !roomUnknown &&
    (mainH < MOBILE_TIMER_COMPACT_MIN_H || (iobShown && iobFit.compactTimer));

  const dropLine =
    question === "worsening" && !noData ? worseningMetaLine(memberName, valueText) : null;

  // Cycle 456: a hero took it (not "Someone else", not a snooze).
  const heroTreating =
    !quiet && treatingBy != null && treatingBy.hero_id != null && !!treatingBy.display_name;
  const byLine = heroTreating ? startedLine(treatingBy, episode.custodyOpenedAt) : null;

  return (
    <div
      className={`${styles.root} ${styles[variant] ?? ""}${
        compactTimer ? ` ${styles.compactTimer}` : ""
      }${variant === "mobile" && iobShown && !picker ? ` ${styles.iobPresent}` : ""}`}
      data-testid="treating-screen"
      role="region"
      aria-label={tag}
      data-custody-kind={episode.custodyKind ?? "none"}
      data-iob-step={picker ? "covered" : iobShown ? iobFit.step : onBoard ? "withheld" : "absent"}
    >
      <div className={styles.top}>
        <div
          className={`${styles.tag}${episode.recoveringFromUrgentLow ? ` ${styles.tagLong}` : ""}`}
        >
          <span className={styles.tagDot} aria-hidden />
          {tag}
        </div>
        {heroTreating && !picker ? (
          <div
            className={styles.byChip}
            data-testid="treating-by-chip"
            data-treating-by={treatingBy?.source}
          >
            <HeroAvatar
              preset={treatingBy?.avatar_preset}
              photo={treatingBy?.avatar_photo ?? null}
              name={treatingBy?.display_name}
              size={variant === "mobile" ? 40 : 38}
            />
            <span className={styles.byText}>
              <span className={styles.byName}>
                {selfTreating
                  ? "You're treating"
                  : `${treatingBy?.display_name} is treating`}
              </span>
              {variant === "mobile" && (
                <span className={styles.bySub}>
                  {selfTreating ? "Everyone can see you have this" : tookItLine(treatingBy)}
                </span>
              )}
            </span>
            {onNotTreater && !selfTreating && (
              <button
                type="button"
                className={styles.notTreater}
                data-testid="treating-not-treater"
                onClick={onNotTreater}
              >
                Not {treatingBy?.display_name}?
              </button>
            )}
          </div>
        ) : picker ? (
          /* Cycle 456 (safety review C3): the sheet covers the main area,
             so the reading stays on screen here, in the status line's
             place, while the question is up — on a "Not Maya?" re-pick too,
             where it takes the chip's place (astra round 2). (A dark feed or
             a held ascent closes the sheet — those lines need the main
             area.) */
          <div className={styles.pickerReading} data-testid="treating-picker-reading">
            <span className="tnum" style={{ color, fontWeight: 800 }}>{valueText}</span>
            <span>{noData ? "No recent reading" : trend.label}</span>
          </div>
        ) : (
          /* Cycle 456: on a phone the sheet below IS the question "who's
             treating?", so the "Someone is treating" line steps aside for
             it and gives its row to the tiles. */
          <div className={styles.who}>{copy.who}</div>
        )}
      </div>

      {picker}

      {!picker && (
      <>
      <div className={styles.main} ref={mainRef}>
        <div className={styles.glucose} data-testid="treating-glucose">
          {/* Cycle 438 (defect 4): the value, the arrow and the trend
              caption are ONE block. The caption used to live in `.meta`, a
              sibling of `.main`, so "Rising fast · just now" sat
              bottom-centre under the whole content area and no longer read
              as belonging to the glucose number. The rest of `.meta` (the
              drop line, the help line, the extension count) is page-level
              context and stays where it was. */}
          <div className={styles.heroBlock}>
          <div className={styles.heroRow} ref={heroRowRef}>
          {noData ? (
            <div
              className={`${styles.value} ${styles.valueNoData}`}
              style={{ color }}
              data-testid="treating-no-data"
            >
              {noDataValueLine(ageMin)}
            </div>
          ) : (
            <div className={`${styles.value} tnum`} style={{ color }}>
              {valueText}
            </div>
          )}
          {/* The arrow carries nothing on a dark feed; its slot goes to
              the age so a three-digit age still fits beside the ring. */}
          {!noData && (
            <TrendArrow
              glyph={trend.glyph}
              zone={zone}
              /* ⚠ Cycle 438: 120 -> 96 on the kiosk. Sized WITH the value
                 against the FALLBACK font (see .kiosk .value) so the hero
                 fits its column on a kiosk whose webfont never loaded. */
              size={variant === "kiosk" ? 96 : 72}
              stale={false}
              weight={700}
            />
          )}
          </div>
          <div className={styles.heroCaption} ref={heroCaptionRef}>
            <span>{noData ? "No recent reading" : trend.label}</span>
            {ageMin != null && (
              <>
                <span className={styles.metaDot} aria-hidden />
                <span className="tnum">{fmtAge(ageMin)}</span>
              </>
            )}
          </div>
          </div>
        </div>
        {/* Cycle 437: two labelled instruments, stacked. The custody
            countdown keeps every behaviour it had as a bare ring — only its
            diameter and its type changed — and the insulin-on-board gauge
            joins it beneath. Neither instrument gates anything: the window,
            the prompt and the two loud actions are unchanged. */}
        <div className={styles.instruments}>
          <div className={styles.timerCard} ref={timerCardRef}>
            {byLine ? (
              <div className={styles.timerText}>
                <div className={styles.timerLabel}>Treatment timer</div>
                <div className={styles.timerBy} data-testid="treating-started-by">
                  <HeroAvatar
                    preset={treatingBy?.avatar_preset}
                    photo={treatingBy?.avatar_photo ?? null}
                    name={treatingBy?.display_name}
                    size={variant === "mobile" ? 28 : 35}
                  />
                  <span className={styles.timerByText}>{byLine}</span>
                </div>
              </div>
            ) : (
              <div className={styles.timerLabel}>Treatment timer</div>
            )}
            <div className={styles.ring} data-testid="treating-ring">
              <svg className={styles.ringSvg} viewBox="0 0 120 120" aria-hidden>
                <circle className={styles.ringTrack} cx="60" cy="60" r={RING_R} />
                <circle
                  className={styles.ringArc}
                  cx="60"
                  cy="60"
                  r={RING_R}
                  strokeDasharray={RING_C}
                  strokeDashoffset={dashOffset}
                />
              </svg>
              <div className={styles.ringLabel}>
                <span className={`${styles.ringTime} tnum`}>
                  {fmtTreatingRemaining(remainingMs)}
                </span>
                <span className={styles.ringSub}>left</span>
              </div>
            </div>
          </div>

          {/* ⚠ Cycle 437: the second instrument is WITHHELD on a 600px
              kiosk stage. Decision 1 dropped 1024x600 as a fit box, but two
              code paths still yield a 600 stage (`useFrameVariant` lines 99
              and 121) and this cycle does not remove them. Measured there:
              .main is 352.2px and the two-instrument column is 386.0 — over
              by 33.8, and no canvas that clears the 13px floor closes that
              (the floor needs >=185px of gauge, which saves only 11px). So a
              600 stage keeps exactly the pre-437 screen: one timer, no
              sub-floor type, no overflow. It degrades, it does not break. */}
          {/* ⚠ Cycle 459: on a PHONE the card is never simply withheld for
              want of room any more: it FITS, at the largest of three steps
              the measured room holds (`chooseIobFit`). It is absent only
              when nothing is on board, and withheld only where even the
              chip beside the compact timer cannot fit (on the sweep set:
              a check-in question up on the shortest phones). */}
          {iobShown && (
            <TreatingIobSlot
              step={iobFit.step}
              variant={variant}
              boluses={boluses}
              basal={basal}
              now={now}
              ringCount={ringCount}
              chipEntries={chipEntries}
              dueBasal={dueBasal}
            />
          )}
        </div>
      </div>

      <div className={styles.meta}>
        {dropLine && <span data-testid="treating-drop">{dropLine}</span>}
        {helpLine && (
          <>
            {dropLine && <span className={styles.metaDot} aria-hidden />}
            <span data-testid="treating-help-line">{helpLine}</span>
          </>
        )}
        {extensions > 0 && (
          <>
            {(dropLine || helpLine) && (
              <span className={styles.metaDot} aria-hidden />
            )}
            <span>
              {extensions === 1 ? "1 extension" : `${extensions} extensions`}
            </span>
          </>
        )}
        {/* Cycle 424: while the member climbs out of an urgent low the low
            rung is latched off server-side. Said here, in the meta row's
            existing slot, so a caregiver holding a window sees WHY no low
            alarm follows the climb (copy only, no layout change). */}
        {episode.ascentLatched && (
          <>
            <span className={styles.metaDot} aria-hidden />
            <span data-testid="ascent-hold">Low alerts held until back in range</span>
          </>
        )}
      </div>

      </>
      )}

      {episode.checkinPending && questionText && (
        <div
          className={styles.checkin}
          data-testid="treating-checkin"
          role="dialog"
          aria-live="assertive"
          aria-label={questionText}
        >
          <div className={styles.question}>
            {questionText}
            {promptLeftMs != null && (
              <span className={`${styles.countdown} tnum`} data-testid="treating-countdown">
                {fmtPromptRemaining(promptLeftMs)}
              </span>
            )}
          </div>
          <div className={styles.answers}>
            <button
              type="button"
              className={styles.answer}
              data-testid="treating-yes"
              disabled={busy === "yes"}
              onClick={() => void send("yes")}
            >
              Yes
            </button>
            <button
              type="button"
              className={`${styles.answer} ${styles.answerNo}`}
              data-testid="treating-no"
              disabled={busy === "no"}
              onClick={() => void send("no")}
            >
              No
            </button>
          </div>
        </div>
      )}

      {reply && (
        <div className={styles.reply} aria-live="polite">
          {reply}
        </div>
      )}
      {quiet && declareError && (
        <div className={styles.reply} aria-live="polite" data-testid="treating-declare-note">
          {declareError}
        </div>
      )}

      <div className={styles.actions}>
        {quiet && onDeclare && (
          <button
            type="button"
            className={`${styles.action} ${styles.declare}`}
            data-testid="treating-declare"
            disabled={declareBusy}
            onClick={onDeclare}
          >
            I'm treating this
          </button>
        )}
        {canAskForHelp && (
          <button
            type="button"
            className={`${styles.action} ${styles.help}`}
            data-testid="treating-help"
            disabled={busy === "help"}
            onClick={() => void send("help")}
          >
            I need help now
          </button>
        )}
        <button
          type="button"
          className={`${styles.action} ${styles.resume}`}
          data-testid="treating-resume"
          disabled={busy === "resume"}
          onClick={() => void send("resume")}
        >
          Resume alerts
        </button>
      </div>
    </div>
  );
}

/** Cycle 459: the IOB card's slot, at the step `chooseIobFit` chose.
 *
 *  ⚠ EVERY STEP IS AN INSTRUMENT, NEVER A CONTROL (cycle 437, re-affirmed
 *  by the operator in cycle 459): no `onTap`, no `onClick`, no role="button"
 *  on any of the three. A tap on an instrument must not open anything over a
 *  live urgent low, and every control on this surface is a custody action.
 *
 *  ⚠ THE FULL STEP IS THE SHIPPED CARD, BYTE FOR BYTE, AND IT IS FIRST. It is
 *  the only step the kiosk and the web ever render, and the cycle-437 source
 *  pins read the first gauge mount in this file. Exported for the step tests,
 *  which render each step directly (a node harness cannot measure a row). */
export function TreatingIobSlot({
  step,
  variant,
  boluses,
  basal,
  now,
  ringCount,
  chipEntries,
  dueBasal,
}: {
  step: IobStep;
  variant: FrameVariant;
  boluses: BolusForRings[];
  basal: BasalContext | null;
  now: number;
  ringCount: number;
  chipEntries: IobChipEntry[];
  dueBasal: BasalContext | null;
}) {
  if (step === "full") {
    return (
            <div className={styles.gaugeSlot} data-iob-slot="full">
            <BolusRings
              boluses={boluses}
              basal={basal}
              now={now}
              /* ⚠ Cycle 437: NO `onTap`. The gauge is an instrument on an
                 alarm surface, never a control — a tap here must not open
                 the dose editor over a live urgent low. */
              gauge={{
                labelPx: 13,
                countdownPx: 13,
                cardLabelPx: 13,
                singleCountdown: true,
                /* Cycle 438 defect 3: "INSULIN ON BOARD" is a card HEADER,
                   so the label + legend column aligns to the top instead of
                   being vertically centred beside the legend. */
                contextTop: true,
                /* Cycle 438 defect 5: the gauge sat hard against the card's
                   left padding. ⚠ Configured through the prop — the size,
                   stroke and radius constants are untouched, four other
                   surfaces depend on them. */
                cardPadLeft: 20,
              }}
            >
              <div className={styles.legend}>
                {boluses.map((b) => (
                  <div className={styles.legendRow} key={b.id}>
                    <span
                      className={styles.legendDot}
                      style={{ background: b.color }}
                      aria-hidden
                    />
                    <span className={styles.legendName}>
                      {b.color === CORRECTION_BOLUS_COLOR
                        ? "Correction"
                        : "Bolus"}
                    </span>
                    <span className={`${styles.legendVal} tnum`}>
                      {b.units}u · {coarseLeft(iobTimeRemainingMs([b], now))}
                    </span>
                  </div>
                ))}
                {basal != null && (
                  <div className={styles.legendRow}>
                    <span
                      className={styles.legendDot}
                      style={{ background: basalRingColor(basal) }}
                      aria-hidden
                    />
                    <span className={styles.legendName}>Long-acting</span>
                    <span className={`${styles.legendVal} tnum`}>
                      {/* ⚠ The canonical formatter, not a hand-rolled
                          `≈${hoursLeft}h`. `basalForContext` returns
                          `hoursLeft` as an UNROUNDED FLOAT and `now` ticks
                          every second, so it is essentially never a whole
                          hour: the hand-rolled form rendered
                          "24u · ≈21.000342777777778h", 196.2px of nowrap
                          text that blew 93px past a 360px phone and widened
                          the kiosk gauge card from 392.6 to 560.6px. Every
                          other basal surface uses this formatter; this was
                          the one place that did not. */}
                      {basal.units}u ·{" "}
                      {formatBasalHoursLeft(basal.hoursLeft, basal.due)}
                    </span>
                  </div>
                )}
              </div>
            </BolusRings>
            </div>
    );
  }

  // Phones only below this line: `chooseIobFit` never returns these steps
  // for the kiosk or the web.
  void variant;

  if (step === "compact") {
    // The same card at the minimum legible ring diameter for its ring count
    // (`MIN_RING_DIAMETER`), with the centre at the `iob-ring-centre`
    // exemption sizes (`COMPACT_CENTRE`, inside the exempt marker
    // `BolusRings` already draws) and ONE legend row per dose: the dot, then
    // the dose and its time left. The type name drops from view (the column
    // is ~140px at 360 wide and "CORRECTION" alone is ~92px) but stays in
    // the row's accessible name, and the dot is the ring's own colour. The
    // card label stays at the 13px floor.
    return (
      <div className={styles.gaugeSlot} data-iob-slot="compact">
        <BolusRings
          boluses={boluses}
          basal={basal}
          now={now}
          gauge={{
            size: compactCanvas(ringCount),
            totalPx: COMPACT_CENTRE.totalPx,
            labelPx: COMPACT_CENTRE.labelPx,
            countdownPx: COMPACT_CENTRE.countdownPx,
            basalTotalPx: COMPACT_CENTRE.basalTotalPx,
            cardLabelPx: 13,
            singleCountdown: true,
            contextTop: true,
            cardPadLeft: 20,
            cardPadY: COMPACT_PAD_Y,
          }}
        >
          <div className={`${styles.legend} ${styles.legendCompact}`}>
            {boluses.map((b) => {
              const name = b.color === CORRECTION_BOLUS_COLOR ? "Correction" : "Bolus";
              const val = `${b.units}u · ${coarseLeft(iobTimeRemainingMs([b], now))}`;
              return (
                <div className={styles.legendRowCompact} key={b.id} aria-label={`${name} ${val}`}>
                  <span className={styles.legendDot} style={{ background: b.color }} aria-hidden />
                  <span className={`${styles.legendValCompact} tnum`} aria-hidden>{val}</span>
                </div>
              );
            })}
            {basal != null && (
              <div
                className={styles.legendRowCompact}
                aria-label={`Long-acting ${basal.units}u · ${formatBasalHoursLeft(basal.hoursLeft, basal.due)}`}
              >
                <span
                  className={styles.legendDot}
                  style={{ background: basalRingColor(basal) }}
                  aria-hidden
                />
                <span className={`${styles.legendValCompact} tnum`} aria-hidden>
                  {basal.units}u · {formatBasalHoursLeft(basal.hoursLeft, basal.due)}
                </span>
              </div>
            )}
          </div>
        </BolusRings>
      </div>
    );
  }

  if (step !== "chip") return null;

  // The floor: `iobChip.ts`'s entries — the sheet's own rule, at most two
  // boluses and one long-acting, each ring coloured by type — in one row
  // labelled "Insulin on board", in the card's slot. A DUE long-acting is
  // not an `iobChipEntries` row (nothing is left on board), but the card
  // draws it as an amber attention ring, so the chip says so too.
  return (
    <div
      className={`${styles.gaugeSlot} ${styles.iobChip}`}
      data-iob-slot="chip"
      data-testid="treating-iob-chip"
      role="group"
      aria-label="Insulin on board"
    >
      <span className={styles.iobChipLabel}>Insulin on board</span>
      {chipEntries.map((e) => (
        <span
          className={styles.iobChipEntry}
          key={e.key}
          data-iob-kind={e.kind}
          aria-label={`${e.label} ${e.amount}`}
        >
          <svg className={styles.iobChipRing} viewBox="0 0 40 40" aria-hidden>
            <circle cx="20" cy="20" r="15" className={styles.iobChipTrack} />
            <circle
              cx="20"
              cy="20"
              r="15"
              fill="none"
              stroke={e.color}
              strokeWidth={6}
              strokeLinecap="round"
              strokeDasharray={CHIP_RING_C}
              strokeDashoffset={CHIP_RING_C * (1 - e.fraction)}
              transform="rotate(-90 20 20)"
            />
          </svg>
          <span className={`${styles.iobChipAmount} tnum`} aria-hidden>
            {e.amount}
          </span>
        </span>
      ))}
      {dueBasal && (
        <span
          className={styles.iobChipEntry}
          data-iob-kind="long_acting_due"
          aria-label={`Long-acting ${dueBasal.units}u due`}
        >
          <svg className={styles.iobChipRing} viewBox="0 0 40 40" aria-hidden>
            <circle
              cx="20"
              cy="20"
              r="15"
              fill="none"
              stroke={basalRingColor(dueBasal)}
              strokeWidth={6}
              opacity={0.55}
            />
          </svg>
          <span className={`${styles.iobChipAmount} tnum`} aria-hidden>
            {dueBasal.units}u · due
          </span>
        </span>
      )}
    </div>
  );
}

const CHIP_RING_C = 2 * Math.PI * 15;
